// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// This module provides a tooltip component that displays a timestamp across
// multiple time zones. It is designed to be used with MUI's Tooltip component
// and displays the user's local time, UTC/GMT, organization time, and an
// optional fourth time zone.
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { DateTime, IANAZone } from 'luxon';
import { PropsWithChildren, useMemo } from 'react';

/**
 * Represents an additional time zone to display in the tooltip.
 */
interface AdditionalTimezone {
  /**
   * The IANA time zone identifier (e.g., "America/Los_Angeles").
   */
  timezone: string | IANAZone;

  /**
   * A description label for this time zone (e.g., "London office").
   */
  description: string;
}

interface Props {
  /**
   * The timestamp to display across multiple time zones.
   */
  timestamp: DateTime;

  /**
   * An optional additional time zone to display in the tooltip.
   */
  additionalTimezone?: AdditionalTimezone;

  /**
   * The placement of the tooltip.
   */
  placement?: TooltipProps['placement'];
}

/**
 * Represents a single time zone entry for display in the tooltip.
 */
interface TimezoneEntry {
  /**
   * The label describing this time zone (e.g., "Your local time").
   */
  label: string;

  /**
   * The DateTime object converted to this time zone.
   */
  dateTime: DateTime;

  /**
   * The short timezone code to display (e.g., "PST", "UTC").
   */
  timezoneCode: string;

  /**
   * Whether this entry represents the user's local time and should be
   * highlighted.
   */
  isLocalTime: boolean;
}

/**
 * Gets the short timezone code from a DateTime object.
 * Returns the offset name (e.g., "PST", "EST", "UTC") or falls back to the
 * offset string (e.g., "UTC+5").
 */
function getTimezoneCode(dateTime: DateTime): string {
  const offsetName = dateTime.offsetNameShort;

  if (offsetName) {
    return offsetName;
  }

  // Fallback to offset string if no short name is available
  const offset = dateTime.offset;
  if (offset === 0) {
    return 'UTC';
  }
  const hours = Math.floor(Math.abs(offset) / 60);
  const sign = offset >= 0 ? '+' : '-';
  return `UTC${sign}${hours}`;
}

/**
 * Converts an IANAZone or string to a zone name string.
 */
function getZoneName(timezone: string | IANAZone): string {
  if (typeof timezone === 'string') {
    return timezone;
  }
  return timezone.name;
}

/**
 * A tooltip component that displays a timestamp across multiple time zones.
 * The tooltip always renders three time zones: the user's local time, UTC/GMT,
 * and the organization time. An optional fourth time zone can be provided.
 *
 * Time zones are ordered chronologically with the earliest time first.
 * The user's local time is always highlighted.
 */
export function MultiTimezoneTooltip(props: PropsWithChildren<Props>) {
  const { user } = useCurrentUser();
  const organizationTimezone = user.organization.timezone;

  const timezoneEntries = useMemo(() => {
    const entries: TimezoneEntry[] = [];

    // Get the user's local timezone
    const localZone = DateTime.local().zoneName;
    const localDateTime = props.timestamp.setZone(localZone);
    entries.push({
      label: 'Your local time',
      dateTime: localDateTime,
      timezoneCode: getTimezoneCode(localDateTime),
      isLocalTime: true,
    });

    // Add UTC/GMT
    const utcDateTime = props.timestamp.setZone('UTC');
    entries.push({
      label: 'UTC/GMT time',
      dateTime: utcDateTime,
      timezoneCode: 'UTC',
      isLocalTime: false,
    });

    // Add organization timezone
    const orgZoneName = getZoneName(organizationTimezone);
    const orgDateTime = props.timestamp.setZone(orgZoneName);
    entries.push({
      label: 'HQ (' + orgZoneName + ')',
      dateTime: orgDateTime,
      timezoneCode: getTimezoneCode(orgDateTime),
      isLocalTime: false,
    });

    // Add optional additional timezone
    if (props.additionalTimezone) {
      const additionalZoneName = getZoneName(props.additionalTimezone.timezone);
      const additionalDateTime = props.timestamp.setZone(additionalZoneName);
      entries.push({
        label: props.additionalTimezone.description,
        dateTime: additionalDateTime,
        timezoneCode: getTimezoneCode(additionalDateTime),
        isLocalTime: false,
      });
    }

    // Sort entries by displayed time (earliest first). Since all entries represent
    // the same instant, we sort by UTC offset (more negative offset = earlier
    // displayed time). For example, UTC-8 (PST) shows an earlier time than UTC+0.
    entries.sort((a, b) => a.dateTime.offset - b.dateTime.offset);

    return entries;
  }, [props.timestamp, props.additionalTimezone, organizationTimezone]);

  const placement = props.placement ?? 'right';

  const tooltipContent = (
    <Stack spacing={0} sx={{ minWidth: 280 }}>
      {timezoneEntries.map((entry, index) => (
        <Box key={entry.label}>
          {index > 0 && <Divider />}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              py: 1.5,
              px: 1,
              bgcolor: entry.isLocalTime ? 'action.hover' : 'transparent',
            }}
          >
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                color={entry.isLocalTime ? 'primary' : 'text.secondary'}
                sx={{ lineHeight: 1.2 }}
              >
                {entry.label}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={entry.isLocalTime ? 600 : 400}
              >
                {entry.dateTime.toFormat('MMM d, yyyy, hh:mm:ss a')}
              </Typography>
            </Stack>
            <Chip
              label={entry.timezoneCode}
              size="small"
              color={entry.isLocalTime ? 'primary' : 'default'}
              variant={entry.isLocalTime ? 'filled' : 'outlined'}
            />
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Tooltip
      arrow
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 10,
            p: 0,
            maxWidth: 'none',
          },
        },
        arrow: {
          sx: {
            color: 'divider',
          },
        },
      }}
      title={tooltipContent}
    >
      <Box component="span" sx={{ cursor: 'pointer' }}>
        {props.children}
      </Box>
    </Tooltip>
  );
}
